import React, { memo, useRef } from "react";
import PropTypes from "prop-types";
//Styles
import styled, { withTheme } from "styled-components";
import Slider from "react-slick";
//Components
import { Title, ScreenReaderText, ScrollMore } from "../../atoms";
import { CustomArrow } from "../../molecules";
//Utils
import { compose } from "../../../utils/utilities";
//Variants
import Default from "./Cards/Default";
import SubWeb from "./Cards/SubWeb";
import Video from "./Cards/Video";

const Header = memo(function Header({
  className,
  data,
  styles,
  urlExtension,
  theme,
  onChangeVideo,
  video
}) {
  const sliderRef = useRef(null);

  const onHandleTabNavigation = e => {
    if (e.key === "Tab" && e.shiftKey) {
      sliderRef.current.slickPrev();
    }

    if (e.key === "Tab") {
      sliderRef.current.slickNext();
    }
  };

  const scrollHeight = window.innerHeight - 80;

  const scrollDown = () => {
    window.scrollTo({
      top: scrollHeight,
      left: 0,
      behavior: "smooth"
    });
  };

  const onHandleEnter = (e, url) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      window.open(url, "_blank");
    }
  };

  const onSelectLayout = (item, key, type) => {
    let isExternaLink = item.url !== undefined && item.url.includes("ua.pt");
    switch (type) {
      case "leftColumns":
        return (
          <SubWeb
            key={key}
            onKeyDown={onHandleTabNavigation}
            onKeyPress={e => onHandleEnter(e, item.url)}
            href={isExternaLink ? item.url : `${urlExtension}${item.url}`}
            native={isExternaLink}
            data={{ url: item.image.url, isLocal: item.image.isLocal }}
            ariaLabel={item.image.alt}
            alt={item.image.alt}
            text={item.description}
            title={item.title}
            styles={styles}
            theme={theme}
          />
        );

      case "video":
        return (
          <Video
            key={key}
            url={video.default ? item.default : item.accessible}
            onClick={onChangeVideo}
            audioDescription={!video.default}
            theme={theme}
            placeholder={item.placeholder}
          />
        );

      default:
        return (
          <Default
            key={key}
            onKeyDown={onHandleTabNavigation}
            onKeyPress={e => onHandleEnter(e, item.url)}
            href={isExternaLink ? item.url : `${urlExtension}${item.url}`}
            native={isExternaLink}
            data={{ url: item.image.url, isLocal: item.image.isLocal }}
            ariaLabel={item.image.alt}
            alt={item.image.alt}
            text={item.description}
            title={item.title}
            styles={styles}
            theme={theme}
          />
        );
    }
  };

  return (
    <section className={className}>
      <ScreenReaderText>
        <Title text="Destaques Institucionais" />
      </ScreenReaderText>

      <Slider
        ref={sliderRef}
        autoplay={false}
        autoplaySpeed={5000}
        useTransform={false}
        slidesToShow={1}
        slidesToScroll={1}
        speed={800}
        dots={false}
        arrows={true}
        infinite={true}
        accessibility={true}
        prevArrow={
          <CustomArrow
            arrowSize="medium"
            ariaLabel="slide anterior"
            left
            darken
          />
        }
        nextArrow={
          <CustomArrow arrowSize="medium" ariaLabel="próximo slide" darken />
        }
      >
        {data.list.map((item, index) => onSelectLayout(item, index, item.type))}
      </Slider>
      <ScrollMore onClick={scrollDown} position="absolute" bottom="-50px" />
    </section>
  );
});

const types = {
  className: PropTypes.string,
  data: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  size: PropTypes.object,
  styles: PropTypes.object,
  theme: PropTypes.object,
  urlExtension: PropTypes.string,
  onChangeVideo: PropTypes.func,
  video: PropTypes.shape({
    default: PropTypes.bool
  })
};

const defaults = {
  video: {
    default: true
  }
};

Header.propTypes = types;
Header.defaultProps = defaults;

const headerWithStyles = styled(Header)`
  position: relative;
  //To Remove Space, slick-slider needs same height as image
  .slick-slider,
  .slick-track,
  img {
    max-height: ${props => props.theme.components.header.maxHeight};
    height: ${props =>
      props.isSubWeb
        ? props.theme.components.header.subWeb.height
        : props.theme.components.header.height};
  }

  .slick-slider {
    overflow-y: hidden;
    &:hover {
      .slick-next,
      .slick-prev {
        background-color: ${props =>
          props.theme.components.header.arrowBackgroundColor};
        i {
          color: ${props => props.theme.components.header.arrowColor};
        }
      }
    }
  }

  .slick-slide {
    height: inherit;
    > div {
      height: inherit;
    }
  }

  /* Medium devices (tablets, less than 992px)*/
  @media (max-width: 991px) {
    .slick-slider,
    .slick-track,
    img {
      height: ${props =>
        props.isSubWeb
          ? props.theme.components.header.subWeb.mobileHeight
          : props.theme.components.header.mobileHeight};
    }
  }

  .onEdgeRightArrow > .slick-next,
  .onEdgeLeftArrow > .slick-prev {
    i {
      color: ${props => props.theme.components.header.arrowColor};
    }
    background-color: ${props =>
      props.theme.components.header.arrowBackgroundColor};
  }

  .slick-next,
  .slick-prev {
    width: 50px;
    height: 50px;
    top: calc(50% - 25px);
    background-color: transparent;

    &:hover {
      background-color: ${props =>
        props.theme.components.header.arrowBackgroundColor};
    }
    &:focus {
      i {
        color: ${props => props.theme.components.header.arrowColor};
      }
      background-color: ${props =>
        props.theme.components.header.arrowBackgroundColor};
    }

    i {
      color: transparent;
    }
  }

  .slick-prev {
    left: 0;
  }

  .slick-next {
    right: 0;
  }

  .slick-next:before,
  .slick-prev:before {
    content: none;
  }
`;

export default compose(withTheme)(headerWithStyles);

//Only used for the Storybook Props Table
export const WithProps = ({ ...props }) => <headerWithStyles {...props} />;

WithProps.propTypes = types;
WithProps.defaultProps = defaults;
